import type { LineString, MultiLineString, Position } from 'geojson';
import type { VectorFeatureCollection } from '../data/model';
import type { AnnotationScreenProjection } from '../annotations/textLayout';

type Point = { readonly x: number; readonly y: number };
const EPSILON = 1e-6;

function finitePoint(point: Point): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

/**
 * A parallel screen path with bounded joins. Unlike the renderer's per-glyph offset,
 * this never walks an unbounded offset-line intersection at a short/sharp bend.
 * Unsafe miters use a normal-bisector join instead of shrinking displacement
 * toward the original line or extending an intersection arbitrarily far.
 */
export function offsetLineLabelPath(
  points: readonly Point[],
  offset: number,
  neighbours: { readonly previous?: Point; readonly next?: Point } = {},
): Point[] {
  const path: Point[] = [];
  const extended = [
    ...(neighbours.previous ? [neighbours.previous] : []),
    ...points,
    ...(neighbours.next ? [neighbours.next] : []),
  ];
  for (const point of extended) {
    const previous = path.at(-1);
    if (!finitePoint(point)) return [];
    if (!previous || Math.hypot(point.x - previous.x, point.y - previous.y) > EPSILON)
      path.push(point);
  }
  if (path.length < 2) return [];
  const trim = (result: Point[]) =>
    result.slice(neighbours.previous ? 1 : 0, neighbours.next ? -1 : undefined);
  if (offset === 0) return trim(path);
  const segments = path.slice(1).map((point, index) => {
    const start = path[index]!;
    const length = Math.hypot(point.x - start.x, point.y - start.y);
    return { x: (point.x - start.x) / length, y: (point.y - start.y) / length, length };
  });
  return trim(
    path.map((point, index) => {
      const previous = segments[index - 1];
      const next = segments[index];
      const segment = previous ?? next!;
      let x = -segment.y * offset;
      let y = segment.x * offset;
      if (previous && next) {
        const denominator = 1 + previous.x * next.x + previous.y * next.y;
        if (denominator > EPSILON) {
          x = (-(previous.y + next.y) * offset) / denominator;
          y = ((previous.x + next.x) * offset) / denominator;
          const cutback = Math.abs(x * previous.x + y * previous.y);
          const length = Math.hypot(x, y);
          if (
            length > 2 * Math.abs(offset) ||
            cutback > Math.min(previous.length, next.length) / 2
          ) {
            x *= Math.abs(offset) / length;
            y *= Math.abs(offset) / length;
          }
        }
      }
      return { x: point.x + x, y: point.y + y };
    }),
  );
}

interface EndpointNeighbours {
  readonly previous?: Position;
  readonly next?: Position;
}
const neighbourCache = new WeakMap<
  VectorFeatureCollection,
  ReadonlyMap<Position[], EndpointNeighbours>
>();

/** Preserve native joins at exact, unambiguous end→start connections, including closed lines. */
function endpointNeighbours(
  data: VectorFeatureCollection,
): ReadonlyMap<Position[], EndpointNeighbours> {
  const cached = neighbourCache.get(data);
  if (cached) return cached;
  const endpoints = new Map<string, { part: Position[]; start: boolean; neighbour: Position }[]>();
  const key = (position: Position) => position[0] + ',' + position[1];
  for (const feature of data.features) {
    const geometry = feature.geometry;
    const parts =
      geometry?.type === 'LineString'
        ? [geometry.coordinates]
        : geometry?.type === 'MultiLineString'
          ? geometry.coordinates
          : [];
    for (const part of parts) {
      if (part.length < 2) continue;
      for (const start of [true, false]) {
        const position = (start ? part[0] : part.at(-1))!;
        let neighbour: Position | undefined;
        for (
          let index = start ? 1 : part.length - 2;
          index >= 0 && index < part.length;
          index += start ? 1 : -1
        ) {
          const candidate = part[index]!;
          if (key(candidate) !== key(position)) {
            neighbour = candidate;
            break;
          }
        }
        if (!neighbour) continue;
        const entries = endpoints.get(key(position)) ?? [];
        entries.push({ part, start, neighbour });
        endpoints.set(key(position), entries);
      }
    }
  }
  const result = new Map<Position[], EndpointNeighbours>();
  for (const entries of endpoints.values()) {
    if (entries.length !== 2 || entries[0]!.start === entries[1]!.start) continue;
    const start = entries.find((entry) => entry.start)!;
    const end = entries.find((entry) => !entry.start)!;
    result.set(start.part, { ...result.get(start.part), previous: end.neighbour });
    result.set(end.part, { ...result.get(end.part), next: start.neighbour });
  }
  neighbourCache.set(data, result);
  return result;
}

interface CachedPaths {
  readonly offset: number;
  readonly projection: readonly number[];
  readonly data: VectorFeatureCollection;
  readonly sourceFeatureIndices: readonly number[];
}
const cache = new WeakMap<VectorFeatureCollection, CachedPaths>();

/** Transient label geometry only; canonical data/properties and saved pixel values are untouched. */
export function lineLabelPaths(
  data: VectorFeatureCollection,
  offset: number,
  projection: AnnotationScreenProjection,
): { readonly data: VectorFeatureCollection; readonly sourceFeatureIndices: readonly number[] } {
  const first = data.features.find(
    (feature) =>
      feature.geometry?.type === 'LineString' || feature.geometry?.type === 'MultiLineString',
  )?.geometry;
  const position =
    first?.type === 'LineString'
      ? first.coordinates[0]
      : first?.type === 'MultiLineString'
        ? first.coordinates[0]?.[0]
        : undefined;
  const coordinate = position ? ([position[0]!, position[1]!] as const) : ([0, 0] as const);
  // Four public projected probes identify the settled ground-plane camera transform.
  const signature = [
    [0, 0],
    [0.001, 0],
    [0, -0.001],
    [0.001, -0.001],
  ].flatMap(([x, y]) => {
    const point = projection.project([coordinate[0] + x!, coordinate[1] + y!]);
    return [point.x, point.y];
  });
  const cached = cache.get(data);
  if (
    cached?.offset === offset &&
    signature.every((value, index) => value === cached.projection[index])
  )
    return cached;
  const features: VectorFeatureCollection['features'] = [];
  const sourceFeatureIndices: number[] = [];
  const neighbours = endpointNeighbours(data);
  function parallel(coordinates: Position[]): Position[] {
    const context = neighbours.get(coordinates);
    const project = (position: Position) => projection.project([position[0]!, position[1]!]);
    const points = coordinates.map((value) => projection.project([value[0]!, value[1]!]));
    const path = offsetLineLabelPath(points, offset, {
      ...(context?.previous ? { previous: project(context.previous) } : {}),
      ...(context?.next ? { next: project(context.next) } : {}),
    });
    const result = path.map((point) => [...projection.unproject(point)]);
    return result.every((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]))
      ? result
      : [];
  }
  for (const [index, feature] of data.features.entries()) {
    const original = feature.geometry;
    let geometry: LineString | MultiLineString;
    if (original?.type === 'LineString') {
      const coordinates = parallel(original.coordinates);
      if (coordinates.length < 2) continue;
      geometry = { type: 'LineString', coordinates };
    } else if (original?.type === 'MultiLineString') {
      const coordinates = original.coordinates.map(parallel).filter((part) => part.length >= 2);
      if (!coordinates.length) continue;
      geometry = { type: 'MultiLineString', coordinates };
    } else continue;
    features.push({ ...feature, geometry });
    sourceFeatureIndices.push(index);
  }
  const result: CachedPaths = {
    offset,
    projection: signature,
    data: { type: 'FeatureCollection', features },
    sourceFeatureIndices,
  };
  cache.set(data, result);
  return result;
}
